// Eskom PARCC — the AI Compliance Engine availability gate (TK, Jul 2026).
//
// AI is rolled out ONE PERSON AT A TIME, the same way Lumen was: nobody is
// switched on by a deploy, and Aziz and Lindiwe turn it on when they are ready.
// That needs three independent layers, and this module is where the client-side
// two are combined into the single boolean a UI surface may branch on.
//
//   1. GLOBAL KILL SWITCH — env on admin-api. Stops everything at once during an
//      incident without editing a grant row per person. Enforced server-side
//      only; the client never sees it (a hidden button is not a control).
//   2. ELIGIBILITY — `AccessGrant.aiEnabled`, granted by an admin on the Users
//      screen. Re-derived server-side from the grant on every /api/ai/* call.
//   3. PREFERENCE — `uiPrefs.aiMode`, the person's own switch in Settings.
//
// WHY THE SPLIT MATTERS. Layer 3 lives in localStorage and is user-writable, so
// it can only ever decide what somebody SEES. Layer 2 decides what they may DO.
// Setting `aiMode: 'on'` in devtools reveals nothing (this module still requires
// the grant) and authorises nothing (the server re-derives its own answer and
// ignores the client entirely). Keeping the two apart is the whole point: a
// preference is not an authorisation boundary, and conflating them would turn a
// convenience toggle into a way to spend money on inference over NDA-covered
// evidence.
//
// Everything fails CLOSED — a still-resolving session, a missing grant row, a
// grant predating the column, or a component rendered outside the preferences
// provider all resolve to "no AI".
import { useMemo } from 'react';
import { useAuth } from './auth';
import { useAiModePref } from './uiPrefs';
import { canUseAi } from './permissions';

export interface AiAvailability {
  /** An admin has granted this person AI (`AccessGrant.aiEnabled`). */
  eligible: boolean;
  /** They have switched it on for themselves (`uiPrefs.aiMode === 'on'`). */
  optedIn: boolean;
  /**
   * Both of the above. **The only flag a UI surface may branch on.** When false,
   * every AI surface is absent — not disabled, absent — so the app renders
   * exactly as it did before the engine existed.
   */
  available: boolean;
}

/**
 * Resolve whether the AI surfaces should render for the signed-in person.
 *
 * Impersonation-aware by construction: `useAuth().grant` is the EFFECTIVE grant,
 * and `impersonationGrant()` deliberately does not carry `aiEnabled`. So an admin
 * previewing as somebody else sees their view but never gains their ability to
 * spend on inference.
 */
export function useAiAvailability(): AiAvailability {
  const { grant } = useAuth();
  const aiMode = useAiModePref();
  return useMemo(() => {
    const eligible = canUseAi(grant);
    const optedIn = aiMode === 'on';
    return { eligible, optedIn, available: eligible && optedIn };
  }, [grant, aiMode]);
}
